import { scaleLinear } from 'd3-scale';
import { seriesSvgMulti, AnySeries } from '@d3fc/d3fc-series';
import { extentLinear } from '@d3fc/d3fc-extent';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { computeGroupedData } from 'utils/components/TimeSeriesChart/groupedChartData';
import { IntraDayProfileDataItem } from './IntraDayProfileData';
import { generateLineSeries, generateVolumeBarSeries } from './generateSeries';
import { percentageFormat } from 'utils/components/TimeSeriesChart/yAxis';
import { generateAreaSeries } from 'utils/components/TimeSeriesChart/area.ts';

const Y_TICKS = 5;
export const getYTicks: () => number = () => Y_TICKS;
export const yTickFormat: (d: number) => string = percentageFormat;

export const computeYDomain: (
    data: IntraDayProfileDataItem[]
) => [number, number] = (data) => {
    return data.length > 0
        ? extentLinear().accessors([
              () => 0,
              (d: IntraDayProfileDataItem) => d.historicalTradedVolume!,
              (d: IntraDayProfileDataItem) => d.actualTradedVolume!,
              (d: IntraDayProfileDataItem) =>
                  d.historicalAuctionAndTradedVolume!,
          ])(data)
        : [0, 1];
};

const getCumulativeScaleFn = (
    dataSeries: IntraDayProfileDataItem[],
    yAxisDomain: [number, number]
) => {
    const cumulativeDomain = extentLinear().accessors([
        () => 0,
        (d: IntraDayProfileDataItem) => d.cumulativeTradedVolume!,
        (d: IntraDayProfileDataItem) => d.historicalCumulativeTradedVolume!,
    ])(dataSeries);

    return scaleLinear().domain(cumulativeDomain).range(yAxisDomain);
};

export const getChartSeries: (
    dataSeries: IntraDayProfileDataItem[],
    chartWidth: number,
    historicalCumulativeGradientId: string,
    cumulativeGradientId: string
) => AnySeries = (
    dataSeries,
    chartWidth,
    historicalCumulativeGradientId,
    cumulativeGradientId
) => {
    const yDomain = computeYDomain(dataSeries);

    const cumulativeScaleFn = getCumulativeScaleFn(dataSeries, yDomain);
    const historicalCumulativeTradedSeries = generateAreaSeries(
        (d: IntraDayProfileDataItem) => d.historicalCumulativeTradedVolume!,
        cumulativeScaleFn,
        historicalCumulativeGradientId,
        'cumulativeExpected'
    );
    const cumulativeTradedSeries = generateAreaSeries(
        (d: IntraDayProfileDataItem) => d.cumulativeTradedVolume!,
        cumulativeScaleFn,
        cumulativeGradientId,
        'cumulativeActual'
    );

    const volatilitySeries = generateLineSeries(
        dataSeries,
        (d: IntraDayProfileDataItem) => d.historicalVolatility!,
        yDomain,
        'volatilityLine'
    );
    const spreadSeries = generateLineSeries(
        dataSeries,
        (d: IntraDayProfileDataItem) => d.historicalSpread!,
        yDomain,
        'spreadLine'
    );

    const tradedVolumeBarSeries = generateVolumeBarSeries(
        dataSeries.length,
        chartWidth,
        'historyBar',
        'actualBar'
    );
    const tradedVolumeBarData = computeGroupedData(
        dataSeries,
        (d: IntraDayProfileDataItem) => ({
            time: d.time,
            actualTradedVolume: d.actualTradedVolume,
            historicalTradedVolume: d.historicalTradedVolume,
            barWidthPercentage: d.barWidthPercentage,
        }),
        ['historicalTradedVolume', 'actualTradedVolume']
    );

    const auctionVolumeBarSeries = generateVolumeBarSeries(
        dataSeries.length,
        chartWidth,
        'auctionBar',
        'historyBar' // no actual auction series so use this as placeholder instead
    );
    const auctionVolumeBarData = computeGroupedData(
        dataSeries,
        (d: IntraDayProfileDataItem) => ({
            time: d.time,
            historicalAuctionAndTradedVolume:
                d.historicalAuctionAndTradedVolume,
            historicalTradedVolume: 0, // no actual auction data so use this as placeholder variable and data instead
            barWidthPercentage: d.barWidthPercentage,
        }),
        ['historicalAuctionAndTradedVolume', 'historicalTradedVolume']
    );

    return seriesSvgMulti()
        .series([
            gridlines(Y_TICKS),
            historicalCumulativeTradedSeries,
            cumulativeTradedSeries,
            auctionVolumeBarSeries,
            tradedVolumeBarSeries,
            volatilitySeries,
            spreadSeries,
        ])
        .mapping(
            (
                data: IntraDayProfileDataItem[],
                index: number,
                series: AnySeries[]
            ) => {
                switch (series[index]) {
                    case auctionVolumeBarSeries:
                        return auctionVolumeBarData;
                    case tradedVolumeBarSeries:
                        return tradedVolumeBarData;
                    case cumulativeTradedSeries:
                        return data.filter(
                            (d) => d.cumulativeTradedVolume !== undefined
                        );
                    case historicalCumulativeTradedSeries:
                        return data.filter(
                            (d) =>
                                d.historicalCumulativeTradedVolume !== undefined
                        );
                    case volatilitySeries:
                        return data.filter(
                            (d) => d.historicalVolatility !== undefined
                        );
                    case spreadSeries:
                        return data.filter(
                            (d) => d.historicalSpread !== undefined
                        );
                    default:
                        return data;
                }
            }
        );
};
